import {
  type BagEntry,
  getItem,
  isSellable,
  type ItemKind,
  type ItemTemplate,
  rarityRank,
  sellValue,
} from '@shefira/shared';

import { filterChips } from './bench.js';
import { addLevelBadge } from './item-icon.js';
import { describeOnHover } from './item-hover.js';
import { formatGold, type MerchantView, merchantRow } from './merchant-row.js';
import { uiFormat, uiText } from './text.js';

/**
 * What the merchant would pay for the bag (ADR 0016), in his own window:
 * each item with its price, one at a time or the whole stack, and the loot
 * (materials) all at once. A piece the smith worked on, or an epic and
 * better, asks a second click: the button turns into « Sûr ? » for a moment.
 * The server judges every sale; this only previews it with `sellValue`.
 */

export interface MerchantSellOptions {
  onSell: (itemId: string, quantity: number) => void;
  /** Several sales sent at once: the window sums up their answers. */
  onSellMany: (sales: readonly { itemId: string; quantity: number }[]) => void;
}

type Filter = 'all' | ItemKind;

const FILTERS: readonly Filter[] = ['all', 'material', 'consumable', 'equipment'];

/** Materials first: the loot one comes to sell; equipment last, the pieces one thinks twice about. */
const KIND_ORDER: Record<ItemKind, number> = { material: 0, consumable: 1, equipment: 2 };

/** How long a « Sûr ? » waits for its second click. */
const CONFIRM_MS = 3000;

interface Sellable {
  entry: BagEntry;
  template: ItemTemplate;
}

export class MerchantSellView {
  private filter: Filter = 'all';
  /** The button waiting for its second click (`<item id>:<quantity>`, or `loot`), and until when. */
  private armed: { key: string; until: number } | undefined;

  constructor(private readonly options: MerchantSellOptions) {}

  render(into: HTMLElement, view: MerchantView): void {
    const sellable = this.sellable(view);
    if (sellable.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'bench__hint';
      empty.textContent = uiText('Rien dans ton sac ne l’intéresse.');
      into.append(empty);
      return;
    }
    into.append(this.lootBar(sellable, view), this.filters(sellable, view));
    const shown = sellable.filter(
      ({ template }) => this.filter === 'all' || template.kind === this.filter,
    );
    for (const item of shown) into.append(this.row(item, view));
  }

  /** The window closed or changed sides: no « Sûr ? » left hanging. */
  reset(): void {
    this.armed = undefined;
  }

  private sellable(view: MerchantView): Sellable[] {
    const found: Sellable[] = [];
    for (const entry of view.inventory?.bag ?? []) {
      const template = getItem(entry.item.templateId);
      if (template !== undefined && isSellable(template)) found.push({ entry, template });
    }
    return found.sort(
      (a, b) =>
        KIND_ORDER[a.template.kind] - KIND_ORDER[b.template.kind] || a.entry.slot - b.entry.slot,
    );
  }

  /** The loot, priced as a whole, sold in one click (and a second to confirm). */
  private lootBar(sellable: readonly Sellable[], view: MerchantView): HTMLElement {
    const loot = sellable.filter(({ template }) => template.kind === 'material');
    const worth = loot.reduce(
      (sum, { entry, template }) => sum + sellValue(template, entry.item.quantity),
      0,
    );
    const bar = document.createElement('div');
    bar.className = 'merchant__loot';
    const label = document.createElement('span');
    label.className = 'merchant__loot-text';
    label.textContent =
      loot.length > 0
        ? uiFormat('Ton butin vaut {gold}.', { gold: formatGold(worth) })
        : uiText('Pas de butin à vendre.');
    const button = this.confirmButton('loot', uiText('Vendre tout le butin'), view, () => {
      this.options.onSellMany(
        loot.map(({ entry }) => ({ itemId: entry.item.id, quantity: entry.item.quantity })),
      );
    });
    button.classList.add('merchant__loot-go');
    button.disabled ||= loot.length === 0;
    bar.append(label, button);
    return bar;
  }

  private filters(sellable: readonly Sellable[], view: MerchantView): HTMLElement {
    // The filter chosen emptied out (all sold): back to everything.
    if (this.filter !== 'all' && !sellable.some(({ template }) => template.kind === this.filter)) {
      this.filter = 'all';
    }
    const labels: Record<Filter, string> = {
      all: uiText('Tout'),
      material: uiText('Matériaux'),
      consumable: uiText('Consommables'),
      equipment: uiText('Équipement'),
    };
    const chips = FILTERS.map((filter) => ({
      value: filter,
      label: labels[filter],
      count: sellable.filter(({ template }) => filter === 'all' || template.kind === filter).length,
    })).filter((chip) => chip.value === 'all' || chip.count > 0);
    return filterChips(chips, this.filter, (filter) => {
      this.filter = filter;
      view.redraw();
    });
  }

  private row({ entry, template }: Sellable, view: MerchantView): HTMLElement {
    const { item } = entry;
    const name =
      item.upgradeLevel > 0
        ? `${uiText(template.name)} +${String(item.upgradeLevel)}`
        : uiText(template.name);
    const { row, slot, icon, text, meta, actions } = merchantRow(template, view.icons, name);
    row.dataset.template = template.id;
    addLevelBadge(icon, item.upgradeLevel);
    if (item.quantity > 1) {
      const quantity = document.createElement('span');
      quantity.className = 'item__qty';
      quantity.textContent = String(item.quantity);
      icon.append(quantity);
    }
    const each = sellValue(template, 1);
    meta.textContent =
      item.quantity > 1
        ? uiFormat('{each} l’unité · {count} dans ton sac', {
            each: formatGold(each),
            count: item.quantity,
          })
        : '';
    const price = document.createElement('span');
    price.className = 'bench__price';
    price.textContent = formatGold(sellValue(template, item.quantity));
    text.append(price);

    const context = () => ({ wearer: view.wearer, details: {}, instance: item });
    describeOnHover(slot, template, context);
    describeOnHover(text, template, context);

    const precious = item.upgradeLevel > 0 || rarityRank(template.rarity) >= rarityRank('epic');
    const sell = (quantity: number, label: string): HTMLButtonElement => {
      const go = () => {
        this.options.onSell(item.id, quantity);
      };
      return precious
        ? this.confirmButton(`${item.id}:${String(quantity)}`, label, view, go)
        : this.button(label, view, go);
    };
    actions.append(sell(1, uiText('Vendre')));
    if (item.quantity > 1) {
      const all = sell(item.quantity, uiText('Tout'));
      all.classList.add('merchant__sell--all');
      actions.append(all);
    }
    return row;
  }

  private button(label: string, view: MerchantView, onClick: () => void): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'smith__go merchant__sell';
    button.textContent = label;
    button.disabled = !view.inReach;
    button.addEventListener('click', onClick);
    return button;
  }

  /** A button that asks « Sûr ? » first, and acts on the second click within `CONFIRM_MS`. */
  private confirmButton(
    key: string,
    label: string,
    view: MerchantView,
    onConfirm: () => void,
  ): HTMLButtonElement {
    const armed = this.armed?.key === key && this.armed.until > Date.now();
    const button = this.button(armed ? uiText('Sûr ?') : label, view, () => {
      if (this.armed?.key === key && this.armed.until > Date.now()) {
        this.armed = undefined;
        onConfirm();
        return;
      }
      this.armed = { key, until: Date.now() + CONFIRM_MS };
      view.redraw();
      window.setTimeout(() => {
        if (this.armed?.key !== key) return;
        this.armed = undefined;
        view.redraw();
      }, CONFIRM_MS);
    });
    if (armed) button.classList.add('merchant__sell--armed');
    return button;
  }
}
